import { z } from 'zod';

/**
 * Billing, the operator's spec verbatim: charge per OLT, everyone bills on
 * the 1st, each OLT's first month is free, and the paying org chooses the
 * period — one, three, six or twelve months — at payment time. Four ways to
 * pay; the network itself never stops for an unpaid bill, only the pages do.
 */
export const BILLING_PERIODS = [1, 3, 6, 12] as const;

/**
 * How the money reached us.
 *
 * `credit` is the odd one and is deliberately here rather than off to one
 * side: an invoice settled out of an organization's prepay balance is still a
 * payment, it still produces a receipt, and it still has to go through the one
 * confirmation path in `store.ts`. What distinguishes it is that the money
 * arrived EARLIER, by one of the other four, and was held in the wallet until
 * there was a bill to spend it on — so the receipt says `Credit` and the
 * wallet ledger says where that credit came from.
 *
 * A `credit` payment is never started by a payer and never reaches a gateway,
 * which is why `POST /payments/:id/check` refuses it the way it refuses a bank
 * transfer: there is no third party to ask.
 */
export const paymentMethodSchema = z.enum(['mpesa', 'stripe', 'paystack', 'bank', 'credit']);
export type PaymentMethod = z.infer<typeof paymentMethodSchema>;

export const paymentStatusSchema = z.enum(['pending', 'confirmed', 'failed', 'rejected']);
export type PaymentStatus = z.infer<typeof paymentStatusSchema>;

/**
 * One payment method as the payer sees it: whether it can be used, and which
 * currencies it can charge in — preset by the gateway (M-PESA: KES only;
 * Stripe/Paystack: USD and KES) and narrowed to what the platform has priced.
 */
export const billingMethodSchema = z.object({
  available: z.boolean(),
  currencies: z.array(z.string()),
});
export type BillingMethod = z.infer<typeof billingMethodSchema>;

/**
 * The card an organization keeps on file for renewals, as the page shows it.
 *
 * DISPLAY FIELDS ONLY, and that is the whole contract: the card itself lives
 * with Paystack, and what this platform holds beyond these is one encrypted
 * authorization token that no route ever returns. Brand, last four and expiry
 * are what a payer needs to recognise their own card; `expired` is computed
 * server-side from the expiry so the page and the renewal tick agree on it.
 * `lastFailure` is the gateway's own words for the last refusal, because "the
 * renewal did not go through" is a question the page has to be able to
 * answer without the operator reading a log.
 */
/**
 * What the renewal tick will do about the CURRENT month, computed server-side
 * from the same rows and rules the tick runs, so the wall and the countdown
 * promise only what is true. "Renews automatically within the hour" from the
 * switch alone was false in exactly the states where the payer had to act.
 *
 *  - `scheduled`   — the next hourly tick charges the card, if a month is owed
 *  - `retrying`    — refused this month; tried again once `nextAttemptAt` passes
 *  - `exhausted`   — refused three times this month; renewals resume next month
 *  - `waiting`     — a renewal has been raised and Paystack has not answered yet
 *  - `charged`     — a renewal already landed this month; nothing more is
 *                    charged until the month turns (an org in arrears stays owed)
 *  - `unavailable` — the platform cannot charge it: Paystack off or without a
 *                    key, billing switched off (no per-OLT rate), or the card's
 *                    currency not priced
 *  - `expired`     — past its expiry month
 *  - `off`         — the payer's switch is off
 */
export const billingCardRenewalSchema = z.enum([
  'scheduled',
  'retrying',
  'exhausted',
  'waiting',
  'charged',
  'unavailable',
  'expired',
  'off',
]);
export type BillingCardRenewal = z.infer<typeof billingCardRenewalSchema>;

export const billingCardSchema = z.object({
  brand: z.string().nullable(),
  cardType: z.string().nullable(),
  bank: z.string().nullable(),
  last4: z.string().nullable(),
  expMonth: z.number().int().nullable(),
  expYear: z.number().int().nullable(),
  /** What a renewal charges in — the currency of the payment that produced the card. */
  currency: z.string(),
  /** The payer's switch: off keeps the card and stops the tick charging it. */
  autoRenew: z.boolean(),
  addedAt: z.number().int(),
  lastChargedAt: z.number().int().nullable(),
  lastAttemptAt: z.number().int().nullable(),
  lastFailure: z.string().nullable(),
  /** Unattended attempts spent this calendar month, out of three. */
  attemptsThisMonth: z.number().int(),
  /** Past its expiry month — the tick will not try it, and the page says so. */
  expired: z.boolean(),
  /** What the tick will do this month — see `billingCardRenewalSchema`. */
  renewal: billingCardRenewalSchema,
  /** When the next unattended attempt can happen; set only while `retrying`. */
  nextAttemptAt: z.number().int().nullable(),
});
export type BillingCard = z.infer<typeof billingCardSchema>;

/**
 * The same card as every member sees it on the status — enough to say
 * "renews automatically on •••• 4081", and what the tick will actually do
 * about it (`renewal`), but nothing about the bank's words for a refusal.
 */
export const billingCardSummarySchema = billingCardSchema.pick({
  brand: true,
  last4: true,
  expMonth: true,
  expYear: true,
  autoRenew: true,
  expired: true,
  renewal: true,
  nextAttemptAt: true,
});
export type BillingCardSummary = z.infer<typeof billingCardSummarySchema>;

/** `PATCH /billing/card` — the only thing a payer changes on a kept card. */
export const updateBillingCardSchema = z.object({ autoRenew: z.boolean() });
export type UpdateBillingCard = z.infer<typeof updateBillingCardSchema>;

export const billingStatusSchema = z.object({
  /**
   * Whether this platform bills this organization at all.
   *
   * False collapses every other figure here to zero and `blocked` to false —
   * see `assembleStatus`, which answers before it computes rather than
   * computing and hiding, so a later reader cannot forget to check.
   */
  billingEnabled: z.boolean(),
  /** Whether the org's people are walled to the billing page. */
  blocked: z.boolean(),
  /** A bank proof is awaiting review — access is provisional, not paid. */
  provisional: z.boolean(),
  currency: z.string(),
  ratePerOltMinor: z.number().int(),
  /** OLTs past their free month, i.e. the ones this month's bill counts. */
  billableOlts: z.number().int(),
  /** Current monthly total in minor units. */
  monthlyMinor: z.number().int(),
  /**
   * OLTs billable in the first UNPAID month (`paidThrough`) and what it will
   * cost (2026-09-18) — the figures the countdown banner names, because that
   * is the month it counts down to. A chassis still in its free month is in
   * here and not in `billableOlts`. Zero means that month bills nothing.
   */
  upcomingBillableOlts: z.number().int(),
  upcomingMonthlyMinor: z.number().int(),
  /** Everything owed up to and including the current month. */
  amountDueMinor: z.number().int(),
  /** Start of the first unpaid month, when one exists. */
  dueSince: z.number().int().nullable(),
  /** Start of the first month NOT yet paid for (= paid through its eve). */
  paidThrough: z.number().int().nullable(),
  /**
   * Money this organization has already paid that is not yet spent on a bill,
   * in `currency`'s minor units.
   *
   * It does NOT reduce `amountDueMinor`, and that is not an oversight. Credit
   * settles INVOICES — whole ones, through the same confirmation path a card
   * payment goes through — and settling an invoice is what moves
   * `paidThrough`, which is what `amountDueMinor` is computed from. A balance
   * that quietly discounted the arrears figure without any invoice changing
   * state would be a second, disagreeing answer to "what do they owe".
   *
   * Shown so a payer who paid the paybill last week can see where their money
   * went, which is the first question a prepay balance has to be able to
   * answer.
   */
  walletMinor: z.number().int(),
  olts: z.array(
    z.object({
      id: z.string(),
      name: z.string(),
      createdAt: z.number().int(),
      /** First month-start this OLT is charged for; in trial until then. */
      billableFrom: z.number().int(),
      /** Superadmin-set: a free OLT never joins any bill. */
      exempt: z.boolean(),
    }),
  ),
  methods: z.object({
    mpesa: billingMethodSchema,
    stripe: billingMethodSchema,
    paystack: billingMethodSchema,
    bank: billingMethodSchema,
  }),
  /**
   * The card on file for renewals, or null when there is none.
   *
   * On the status — which every member reads — so the countdown banner and
   * the page can say the bill will settle itself rather than sending people
   * to pay by hand a bill that a card is about to pay. Brand and last four
   * only; the full card view is `GET /billing/card`, behind `billing.manage`.
   */
  card: billingCardSummarySchema.nullable(),
});
export type BillingStatus = z.infer<typeof billingStatusSchema>;

export const billingQuoteSchema = z.object({
  months: z.number().int(),
  amountMinor: z.number().int(),
  currency: z.string(),
  periodStart: z.number().int(),
  periodEnd: z.number().int(),
});
export type BillingQuote = z.infer<typeof billingQuoteSchema>;

export const paymentRowSchema = z.object({
  id: z.string(),
  /**
   * The payment's own number — what an operator quotes, and its address.
   *
   * A payment had none at all before this. The list identified one by the
   * instant it was created, which is the single thing about a payment that
   * nobody says out loud. See `documentNumber` below for how it is spelled.
   */
  number: z.number().int(),
  orgId: z.string(),
  months: z.number().int(),
  amountMinor: z.number().int(),
  currency: z.string(),
  method: paymentMethodSchema,
  status: paymentStatusSchema,
  reference: z.string().nullable(),
  hasProof: z.boolean(),
  periodStart: z.number().int().nullable(),
  periodEnd: z.number().int().nullable(),
  note: z.string().nullable(),
  createdAt: z.number().int(),
  confirmedAt: z.number().int().nullable(),
});
export type PaymentRow = z.infer<typeof paymentRowSchema>;

/**
 * An invoice: what the platform owner asked an organization for. Distinct
 * from a payment, which is money that moved — an invoice can be corrected
 * or voided without rewriting that record.
 */
export const invoiceStatusSchema = z.enum(['draft', 'issued', 'paid', 'void']);
export type InvoiceStatus = z.infer<typeof invoiceStatusSchema>;

export const invoiceSchema = z.object({
  id: z.string(),
  /**
   * The invoice number AND the address of the invoice, because they are one
   * number: `INV-001834` is the document at `/invoice/1834/<token>`. See
   * `documentNumber`.
   */
  number: z.number().int(),
  orgId: z.string(),
  orgName: z.string().nullable(),
  status: invoiceStatusSchema,
  amountMinor: z.number().int(),
  currency: z.string(),
  periodStart: z.number().int().nullable(),
  periodEnd: z.number().int().nullable(),
  dueAt: z.number().int().nullable(),
  note: z.string().nullable(),
  paidAt: z.number().int().nullable(),
  createdAt: z.number().int(),
});
export type Invoice = z.infer<typeof invoiceSchema>;

/**
 * How a billing document's number is spelled — and why it is only a spelling.
 *
 * There is ONE number per document: a plain integer, allocated once and never
 * reused. Everything else is a rendering of it. `INV-001834` is what goes on
 * the bill; `1834` is what goes in `/invoice/1834/<token>`. Same fact, so the
 * printed document and the URL can no longer disagree — which is the whole of
 * what the operator asked for when they said to use id numbers in the URL.
 *
 * It REPLACED `INV-2026-0001`, which was year-scoped text derived from
 * `max(number) + 1` inside its year. Two things got better and one is worth
 * saying out loud rather than glossing:
 *
 *  - Better: `max + 1` REUSED a number. Delete the year's highest draft and
 *    the next invoice took its number back, so `INV-2026-0007` could name two
 *    different documents over time. The counter behind these numbers
 *    (`services/numbering.ts` — the same one behind `/olts/N`) only ever moves
 *    forward, so a number names one document for ever.
 *  - Better: the number is sayable in a URL, so an operator reading a bill
 *    number down a phone is reading the address of the bill.
 *  - Honest: because the counter never reuses, DELETING A DRAFT spends a
 *    number and leaves a hole. Nothing an organization has seen can leave one
 *    — an issued invoice is voided and kept, never deleted, and `deleteInvoice`
 *    enforces that — so the sequence an audit walks is still gapless. A number
 *    missing from it is a draft that was never issued to anybody, which is a
 *    better record than one that could quietly name two documents.
 *
 * Six digits is a floor, not a ceiling: the padding stops mattering above a
 * million and the number simply gets longer. Nothing parses these back.
 */
export const DOCUMENT_NUMBER_PREFIX = { invoice: 'INV-', payment: 'RCP-' } as const;

/** Which of the two documents a number belongs to. */
export type DocumentKind = keyof typeof DOCUMENT_NUMBER_PREFIX;

export const documentNumber = (kind: DocumentKind, n: number): string =>
  `${DOCUMENT_NUMBER_PREFIX[kind]}${String(n).padStart(6, '0')}`;

/** `INV-001834` — an invoice. */
export const invoiceNumber = (n: number): string => documentNumber('invoice', n);

/** `RCP-000042` — the receipt for one payment. */
export const receiptNumber = (n: number): string => documentNumber('payment', n);

/** What a public link points at: a bill to pay, or a receipt for one paid. */
export const shareTargetKindSchema = z.enum(['invoice', 'payment']);
export type ShareTargetKind = z.infer<typeof shareTargetKindSchema>;

/**
 * The public links on one document, as the OPERATOR sees them.
 *
 * Everything here answers the four questions the operator has after sending a
 * bill or a receipt: is there a link, when did I make it, when does it die,
 * and has anybody opened it. The token is deliberately absent — it is returned
 * exactly once, by the request that mints it, and is never readable again from
 * anywhere.
 *
 * It is a SUMMARY rather than one row, because a document can have more than
 * one link alive at a time. Opening the public page from the operator's own
 * View button mints one, and it has to do that without killing the link that
 * was emailed to the payer last week. So: `links` is how many are live,
 * `createdAt` is the oldest of them (when this document first became public),
 * and the view figures are the totals across all of them.
 *
 * There is no expiry, because there is no expiry: a link stays live until it
 * is revoked, so that a payer can come back to the bill months later and still
 * find it. Revoking takes every link on the document away in a single action,
 * and that action is now the ONLY thing that ends one — which is why an
 * interface showing this summary must show the revoke beside it, and why
 * `viewCount` is not decoration. "It has been opened four times and I only
 * sent it to one person" is the whole early-warning system.
 */
export const shareLinkSchema = z.object({
  /** The invoice id or the payment id — whichever `target` says. */
  targetId: z.string(),
  target: shareTargetKindSchema,
  /** How many links are live on this document right now. Always ≥ 1. */
  links: z.number().int(),
  createdAt: z.number().int(),
  /** Opens, counted rather than logged one audit row per refresh. */
  viewCount: z.number().int(),
  firstViewedAt: z.number().int().nullable(),
  lastViewedAt: z.number().int().nullable(),
});
export type ShareLink = z.infer<typeof shareLinkSchema>;

/** The mint response: the only time the URL exists outside the recipient's inbox. */
export const shareLinkMintedSchema = shareLinkSchema.extend({
  url: z.string(),
});
export type ShareLinkMinted = z.infer<typeof shareLinkMintedSchema>;

/**
 * How a mint treats the links that are already live on the document.
 *
 * `replace` is the Share button: the operator is producing a URL to SEND, and
 * an address sent to the wrong inbox has to be recoverable by sharing again —
 * so the previous link stops working in the same instant. The interface warns
 * before doing it.
 *
 * `add` is the View button: the operator is opening the page themselves to
 * look at it. Rotating the token there would silently kill the link the payer
 * is holding, which is a support call caused by a read-only action, so this
 * one leaves every existing link alone.
 */
export const shareMintModeSchema = z.enum(['replace', 'add']);
export type ShareMintMode = z.infer<typeof shareMintModeSchema>;

export const shareMintRequestSchema = z.object({
  mode: shareMintModeSchema.optional(),
});
export type ShareMintRequest = z.infer<typeof shareMintRequestSchema>;

/** How a link can be delivered to the person who has to pay or keep it. */
export const shareSendChannelSchema = z.enum(['email', 'sms']);
export type ShareSendChannel = z.infer<typeof shareSendChannelSchema>;

/**
 * Send one document's link to a person the OPERATOR names.
 *
 * `to` is typed at send time and is never inferred from anything the platform
 * already holds. That is deliberate and it is the difference between this and
 * every other message the platform sends: the org's admins are on file and are
 * emailed automatically when a bill is issued, but the person who actually
 * pays it — a finance office, an accountant, whoever holds the card — is not a
 * row anywhere here. Guessing at one from fleet data would be sending a
 * financial document to an address nobody chose.
 *
 * `mode` defaults to `add` rather than to `replace`, which is the opposite of
 * the Share button, and for a reason that only shows up once links are
 * permanent: sending to somebody's phone AND their email has to leave both
 * working, and re-sending after "it never arrived" must not break the copy
 * that did. `replace` is still available for the deliberate "that address was
 * wrong, kill what I sent".
 */
export const shareSendRequestSchema = z.object({
  channel: shareSendChannelSchema,
  /** An email address or an international phone number, per `channel`. */
  to: z.string().min(3).max(254),
  mode: shareMintModeSchema.optional(),
});
export type ShareSendRequest = z.infer<typeof shareSendRequestSchema>;

/**
 * What the operator is told after pressing Send.
 *
 * Never the URL. The link exists in the recipient's inbox and in nothing else
 * this platform will hand back — an operator who wants the address itself
 * presses Share, which is the one call that returns one.
 *
 * `delivered` is honest about what each channel can actually promise.
 * Email is queued to a durable outbox and sent by a worker, so `false` there
 * means "accepted, not yet handed to a server" and `deliveryId` is the row to
 * watch. SMS is sent inline, so `false` means the gateway said no — or did not
 * answer, which is its own sentence in `detail`.
 */
export const shareSendResultSchema = shareLinkSchema.extend({
  channel: shareSendChannelSchema,
  /** The address or number as it was actually used, normalised. */
  to: z.string(),
  delivered: z.boolean(),
  detail: z.string(),
  /** The delivery or message record this send wrote, for looking it up after. */
  deliveryId: z.string().nullable(),
});
export type ShareSendResult = z.infer<typeof shareSendResultSchema>;

export const upsertInvoiceSchema = z.object({
  orgId: z.string().min(1).optional(),
  amountMinor: z.coerce.number().int().min(0).max(1_000_000_000).optional(),
  currency: z.string().regex(/^[A-Z]{3}$/).optional(),
  periodStart: z.coerce.number().int().nullable().optional(),
  periodEnd: z.coerce.number().int().nullable().optional(),
  dueAt: z.coerce.number().int().nullable().optional(),
  note: z.string().max(500).nullable().optional(),
  status: invoiceStatusSchema.optional(),
});
export type UpsertInvoiceRequest = z.infer<typeof upsertInvoiceSchema>;

/**
 * What a document was priced from, frozen at the moment it was raised.
 *
 * This is stored — on `invoices.billing_basis` and `billing_payments.
 * billing_basis` — and that is a deliberate reversal. The counts used to be
 * recomputed from the org's CURRENT chassis every time a document was opened,
 * on the reasoning that a stored copy is a second version of the truth. That
 * reasoning is right for a live figure and wrong for a record: the invoice's
 * `amountMinor` is frozen, so recomputing the lines meant that removing an
 * OLT, moving one between organizations or toggling `billingExempt` silently
 * rewrote what a historical invoice SAID it was for while the total it asked
 * for stayed put. A document that contradicts itself, for money, after the
 * fact.
 *
 * An issued invoice is a record of what was asked for, so what it was priced
 * from is part of the record. The org's chassis today answer a different
 * question — what they owe NOW — and `assembleStatus` still answers that from
 * the live rows.
 *
 * Null on rows raised before this column existed: nothing recorded their
 * basis, so those documents still recompute from the current chassis, which is
 * the only answer available for them.
 */
export const billingBasisSchema = z.object({
  /** The months the period covers, and the chassis billable in each. */
  months: z.array(
    z.object({ monthStart: z.number().int(), oltCount: z.number().int() }),
  ),
  /** The headline count — billable chassis when the document was raised. */
  oltCount: z.number().int(),
});
export type BillingBasis = z.infer<typeof billingBasisSchema>;

/**
 * One line of a document — a month of service, priced from the chassis that
 * were billable in it.
 *
 * Derived from the document's frozen `billingBasis` and its own stored total,
 * so reprinting an invoice a year later produces the same lines it printed on
 * the day it was raised. See `billingBasisSchema` for why that is stored.
 *
 * `unitMinor` is null when the stored total does not divide evenly by the
 * OLT-months in the period — a hand-typed invoice, or a rate that moved
 * mid-period. In that case there is no honest per-OLT figure to print and the
 * document shows the total alone rather than inventing one.
 */
export const documentLineSchema = z.object({
  /** Start of the month this line covers, UTC. */
  monthStart: z.number().int(),
  /** Chassis that were billable in that month. */
  oltCount: z.number().int(),
  unitMinor: z.number().int().nullable(),
  amountMinor: z.number().int().nullable(),
});
export type DocumentLine = z.infer<typeof documentLineSchema>;

/**
 * One thing that happened to a document, for the status history.
 *
 * `actor` is null for an organization reading its own invoice: the trail names
 * the platform staff member who raised or edited it, and a tenant has no
 * business learning the operator's staff list from a billing page. The event
 * and when it happened are the parts they are entitled to.
 */
export const documentEventSchema = z.object({
  at: z.number().int(),
  /** A short phrase, already humanised: "Issued", "Marked paid", "Voided". */
  label: z.string(),
  actor: z.string().nullable(),
  detail: z.string().nullable(),
});
export type DocumentEvent = z.infer<typeof documentEventSchema>;

/** How a payer settles this document, assembled from what is configured. */
export const paymentInstructionSchema = z.object({
  method: paymentMethodSchema,
  label: z.string(),
  detail: z.string(),
  /**
   * What the payer types as the account reference, when there is one to type.
   *
   * The document's own number. Null for the methods that have no such field —
   * a card checkout carries its own reference — and null on the one call that
   * has no document in hand. Carried as a field of its own rather than only
   * inside `detail` so a page can print it as a value to copy.
   */
  reference: z.string().nullable(),
});
export type PaymentInstruction = z.infer<typeof paymentInstructionSchema>;

/** The branding a document is printed under — the platform's, or the org's over it. */
export const documentBrandSchema = z.object({
  displayName: z.string(),
  tagline: z.string().nullable(),
  logoDataUri: z.string().nullable(),
  accentColor: z.string().nullable(),
  supportEmail: z.string().nullable(),
  supportUrl: z.string().nullable(),
  footerNote: z.string().nullable(),
});
export type DocumentBrand = z.infer<typeof documentBrandSchema>;

/** Everything one invoice needs to answer a question about itself. */
export const invoiceDetailSchema = invoiceSchema.extend({
  orgSlug: z.string().nullable(),
  updatedAt: z.number().int(),
  /** A user's label, or `billing-run` when the monthly run raised it. */
  createdBy: z.string().nullable(),
  /** The payment that settled it, when one did. */
  payment: paymentRowSchema.nullable(),
  lines: z.array(documentLineSchema),
  /** Billable chassis in the period's FIRST month — what it was priced from. */
  oltCount: z.number().int(),
  history: z.array(documentEventSchema),
  instructions: z.array(paymentInstructionSchema),
  brand: documentBrandSchema,
});
export type InvoiceDetail = z.infer<typeof invoiceDetailSchema>;

/** Everything one payment needs to answer a question about itself. */
export const paymentDetailSchema = paymentRowSchema.extend({
  orgName: z.string().nullable(),
  orgSlug: z.string().nullable(),
  /** The invoices this payment settled — `invoices.payment_id` points back. */
  invoices: z.array(invoiceSchema),
  lines: z.array(documentLineSchema),
  oltCount: z.number().int(),
  history: z.array(documentEventSchema),
  brand: documentBrandSchema,
});
export type PaymentDetail = z.infer<typeof paymentDetailSchema>;

/**
 * The prepay wallet: what an organization has paid that is not yet spent.
 *
 * The operator's customers PREPAY — money arrives at the paybill with no
 * invoice behind it, sometimes before there is a bill to put it against.
 * `organizations.account_number` exists for exactly that reason ("the ONLY
 * thing that can route money that arrives with no invoice behind it"), and
 * until there was somewhere to hold that money there was nothing to do with it
 * but guess at an invoice or refuse it.
 *
 * A LEDGER rather than a balance column, and that shape is the whole design:
 *
 *  - Every movement is a row, positive or negative, and the balance is their
 *    sum. A mutated total has no answer to "why is it this number", which is
 *    the only question anybody ever asks about a balance that looks wrong.
 *  - Rows are never edited and never deleted. Undoing a movement is a second,
 *    opposite row naming the first (`reversalOf`), so credit spent on an
 *    invoice and returned when that invoice was voided is TWO facts in the
 *    record rather than one fact overwritten.
 *  - The balance may never go below zero. Enforced when spending, under a
 *    lock, against the summed ledger — never against a cached total.
 */
export const walletEntryKindSchema = z.enum([
  /** Money arrived and became credit — a C2B paybill payment, or an operator's adjustment. */
  'topup',
  /** Credit was spent settling one invoice. */
  'spend',
  /** A spend returned, because the invoice it settled was voided. */
  'refund',
  /** Credit moved OUT of this organization, because it had been applied to the wrong one. */
  'transfer_out',
  /** Credit moved IN from another organization, for the same reason. */
  'transfer_in',
]);
export type WalletEntryKind = z.infer<typeof walletEntryKindSchema>;

export const walletEntrySchema = z.object({
  id: z.string(),
  orgId: z.string(),
  kind: walletEntryKindSchema,
  /** Signed minor units: positive puts money in, negative takes it out. */
  amountMinor: z.number().int(),
  currency: z.string(),
  /** The invoice this entry was spent on, or returned from. */
  invoiceId: z.string().nullable(),
  /** The payment row that recorded the settlement this entry paid for. */
  paymentId: z.string().nullable(),
  /** The M-PESA C2B message that funded this entry. */
  claimId: z.string().nullable(),
  /** The entry this one undoes — a refund names the spend it returns. */
  reversalOf: z.string().nullable(),
  note: z.string().nullable(),
  createdAt: z.number().int(),
});
export type WalletEntry = z.infer<typeof walletEntrySchema>;

export const walletSchema = z.object({
  orgId: z.string(),
  currency: z.string(),
  balanceMinor: z.number().int(),
  entries: z.array(walletEntrySchema),
});
export type Wallet = z.infer<typeof walletSchema>;

/**
 * One M-PESA C2B message, and what became of it.
 *
 * Recorded whether or not it was believed. A message that was REFUSED is the
 * one an operator most needs to be able to look at — "a customer says they
 * paid and nothing happened" is answered by finding their TransID here and
 * reading `note` — and a forged message thrown away silently would leave that
 * question unanswerable in either direction.
 */
export const c2bClaimStatusSchema = z.enum([
  /**
   * Arrived, and not money. Either nothing has confirmed it is real, or it is
   * real and names no organization this platform can find. A human decides.
   */
  'held',
  /** Confirmed real and applied to an organization's wallet. */
  'credited',
  /** Refused, for the reason in `note`. Never becomes money. */
  'rejected',
]);
export type C2bClaimStatus = z.infer<typeof c2bClaimStatusSchema>;

/**
 * Where a claim stands with Safaricom — the question `status` cannot answer.
 *
 * `held` covers three situations that need three different actions from an
 * operator, and telling them apart is the difference between "fix the
 * credentials", "wait" and "this was a forgery". Derived from the claim's
 * columns rather than stored, so it can never disagree with them.
 */
export const c2bVerifyStateSchema = z.enum([
  /** Safaricom's Transaction Status result confirmed it: receipt and amount agree. */
  'confirmed',
  /**
   * Safaricom answered and CONTRADICTED it — no such transaction, or a
   * different receipt or amount. The only state that is evidence of a forgery.
   *
   * Was `refused`, which read as "somebody refused it" and was set by any
   * negative result including Safaricom's own queue TIMING OUT. A genuine
   * payment displayed to an operator as one Safaricom had denied.
   */
  'disowned',
  /**
   * Safaricom answered and proved nothing: no receipt, no amount, or a
   * transaction they call Reversed. Needs a person, and is not a forgery.
   */
  'inconclusive',
  /** The query is out and the verdict has not come back yet. */
  'waiting',
  /** The query went out and Safaricom never answered, or timed out on it. */
  'unanswered',
  /** Safaricom was never asked — gateway off, no initiator credentials, or Daraja unreachable. */
  'unchecked',
]);
export type C2bVerifyState = z.infer<typeof c2bVerifyStateSchema>;

export const c2bClaimSchema = z.object({
  id: z.string(),
  /** What Safaricom has said about it. `held` alone does not say which. */
  verifyState: c2bVerifyStateSchema,
  /** Safaricom's own receipt number, e.g. `RKTQDM7W6S`. Unique — the replay guard. */
  transId: z.string(),
  status: c2bClaimStatusSchema,
  amountMinor: z.number().int(),
  currency: z.string(),
  /** What the payer typed as the account number, verbatim. */
  billRef: z.string().nullable(),
  /**
   * The payer's number as Safaricom sent it — already masked by them
   * (`25470****149`) on the shortcodes that mask. Stored as received: this
   * platform neither unmasks it nor invents masking of its own.
   */
  msisdn: z.string().nullable(),
  payerName: z.string().nullable(),
  /** The organization the money was applied to, once one is known. */
  orgId: z.string().nullable(),
  orgName: z.string().nullable(),
  /** The organization it was applied to BEFORE a superadmin moved it. */
  reassignedFromOrgId: z.string().nullable(),
  /**
   * How many times this money has been moved between organizations.
   *
   * The three `reassigned*` columns are single-valued, so a second move
   * overwrites the first and the row would then claim one move where there
   * were two. A count cannot be overwritten into a lie: `2` beside two audit
   * entries agrees with them, and `2` beside one does not — which is exactly
   * the disagreement an audit trail exists to surface.
   */
  reassignCount: z.number().int(),
  /**
   * When this money was accepted as real, and by what mechanism —
   * `daraja` (Safaricom's own Transaction Status result) or `operator`.
   */
  verifiedAt: z.number().int().nullable(),
  verifiedVia: z.string().nullable(),
  /** Why it is held or refused, in a sentence an operator can act on. */
  note: z.string().nullable(),
  receivedAt: z.number().int(),
  creditedAt: z.number().int().nullable(),
});
export type C2bClaim = z.infer<typeof c2bClaimSchema>;
